import { Injectable } from '@angular/core';

type SortDirection = 'asc' | 'desc';

interface KeepOnTop {
  keepOnTop?: boolean;
}

@Injectable()
export class OrderingService {
  order<T extends object>(
    list: readonly T[] | null | undefined,
    propertyName: string,
    direction: SortDirection = 'asc',
    caseSensitive = true
  ): T[] {
    if (!list?.length) {
      return [];
    }

    const prop = propertyName?.trim();
    if (!prop) {
      return [...list];
    }

    const keepOnTop = list.filter(
      (x): x is T & KeepOnTop =>
        typeof x === 'object' &&
        x !== null &&
        (x as KeepOnTop).keepOnTop === true
    );

    const rest = list.filter(x => !(x as KeepOnTop).keepOnTop);

    const sorted = [...rest].sort((a, b) => {
      const av = this.getProperty(a, prop);
      const bv = this.getProperty(b, prop);

      const cmp = this.compareValues(av, bv, caseSensitive);
      return direction === 'asc' ? cmp : -cmp;
    });

    return [...keepOnTop, ...sorted];
  }

  private compareValues(
    a: unknown,
    b: unknown,
    caseSensitive: boolean
  ): number {
    if (a == null && b == null) {
      return 0;
    }
    if (a == null) {
      return -1;
    }
    if (b == null) {
      return 1;
    }

    const an =
      typeof a === 'number' ? a : typeof a === 'string' ? Number(a) : NaN;
    const bn =
      typeof b === 'number' ? b : typeof b === 'string' ? Number(b) : NaN;

    const aIsNum = Number.isFinite(an);
    const bIsNum = Number.isFinite(bn);

    if (aIsNum && bIsNum) {
      return an < bn ? -1 : an > bn ? 1 : 0;
    }

    const as = String(a);
    const bs = String(b);

    if (!caseSensitive) {
      const al = as.toLocaleLowerCase();
      const bl = bs.toLocaleLowerCase();
      return al < bl ? -1 : al > bl ? 1 : 0;
    }

    return as < bs ? -1 : as > bs ? 1 : 0;
  }

  private getProperty(obj: object, path: string): unknown {
    return path.split('.').reduce<unknown>((result, key) => {
      if (result == null || typeof result !== 'object') {
        return undefined;
      }
      return (result as Record<string, unknown>)[key];
    }, obj);
  }
}
